import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Cloud, Users, BedDouble, TrendingUp, Target, Calendar, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SLIDES = [
  "metrics",
  "goal",
  "occupancy_week",
  "weather",
  "events",
  "suggestions",
];

const TVMode = () => {
  const [metrics, setMetrics] = useState(null);
  const [goal, setGoal] = useState(null);
  const [weather, setWeather] = useState(null);
  const [events, setEvents] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [time, setTime] = useState(new Date());
  const intervalRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    const fetchAll = async () => {
      const endpoints = [
        `${API}/api/dashboard/metrics`,
        `${API}/api/analytics/revenue-goal`,
        `${API}/api/analytics/weather-sitges?days=7`,
        `${API}/api/analytics/upcoming-events?days_ahead=14`,
        `${API}/api/ai/revenue-suggestions/cached`,
        `${API}/api/analytics/demand-forecast?days=7`,
      ];
      const results = await Promise.allSettled(
        endpoints.map((url) => axios.get(url, { headers })),
      );
      const [m, g, w, e, s, f] = results;
      if (m.status === "fulfilled") setMetrics(m.value.data);
      else setMetrics({}); // unblock loader even on failure
      if (g.status === "fulfilled") setGoal(g.value.data);
      if (w.status === "fulfilled") setWeather(w.value.data);
      if (e.status === "fulfilled") setEvents(e.value.data?.events || []);
      if (s.status === "fulfilled") setSuggestions(s.value.data);
      if (f.status === "fulfilled") setForecast(f.value.data?.forecast || []);
    };
    fetchAll();
    const refresh = setInterval(fetchAll, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slide rotation every 20 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentSlide((s) => (s + 1) % SLIDES.length);
    }, 20000);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (!metrics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-16 h-16 text-white animate-spin" />
      </div>
    );
  }

  const slide = SLIDES[currentSlide];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-12" data-testid="tv-mode">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold">Port Sitges Hotel</h1>
          <p className="text-xl text-slate-300">Centro de Mando · Tiempo Real</p>
        </div>
        <div className="text-right">
          <p className="text-5xl font-bold tabular-nums">{time.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</p>
          <p className="text-lg text-slate-300">{time.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
      </div>

      {/* Slide content */}
      <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 min-h-[60vh] border border-white/10">
        {slide === "metrics" && (
          <div className="grid grid-cols-2 gap-8 h-full">
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-700/10 rounded-2xl p-10 flex flex-col items-center justify-center">
              <BedDouble className="w-16 h-16 text-emerald-300 mb-4" />
              <p className="text-2xl text-emerald-200 mb-2">Ocupación Hoy</p>
              <p className="text-8xl font-bold text-emerald-100">{Math.round(metrics.occupancy_rate)}%</p>
              <p className="text-xl text-emerald-300 mt-3">{metrics.occupied} de {metrics.total_rooms} hab.</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-700/10 rounded-2xl p-10 flex flex-col items-center justify-center">
              <TrendingUp className="w-16 h-16 text-amber-300 mb-4" />
              <p className="text-2xl text-amber-200 mb-2">Producción Hoy</p>
              <p className="text-7xl font-bold text-amber-100">€{Math.round(metrics.total_revenue).toLocaleString("es-ES")}</p>
              <p className="text-xl text-amber-300 mt-3">ADR: €{Math.round(metrics.adr)} · RevPAR: €{Math.round(metrics.revpar)}</p>
            </div>
          </div>
        )}

        {slide === "goal" && goal && (
          <div className="h-full flex flex-col">
            <div className="flex items-center gap-4 mb-6">
              <Target className="w-12 h-12 text-blue-300" />
              <h2 className="text-5xl font-bold">Camino a €{(goal.production.goal / 1_000_000).toFixed(1).replace(".", ",")}M</h2>
            </div>
            <div className="bg-white/10 rounded-xl p-8 mb-6">
              <div className="flex justify-between text-xl mb-3">
                <span>Producción 2026 (Ene-Dic)</span>
                <span className="font-bold text-3xl">€{Math.round(goal.production.projected_full_year).toLocaleString("es-ES")}</span>
              </div>
              <div className="h-6 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-300 transition-all duration-1000"
                  style={{ width: `${Math.min(100, (goal.production.projected_full_year / goal.production.goal) * 100)}%` }}
                />
              </div>
              <p className="text-right mt-2 text-2xl font-bold text-blue-200">
                {((goal.production.projected_full_year / goal.production.goal) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-red-500/20 rounded-xl p-6 text-center">
                <p className="text-lg text-red-200">Brecha</p>
                <p className="text-3xl font-bold text-red-100">€{goal.production.gap_to_goal.toLocaleString("es-ES")}</p>
              </div>
              <div className="bg-amber-500/20 rounded-xl p-6 text-center">
                <p className="text-lg text-amber-200">Necesario/día</p>
                <p className="text-3xl font-bold text-amber-100">€{Math.round(goal.production.daily_needed_to_reach_goal).toLocaleString("es-ES")}</p>
              </div>
              <div className="bg-emerald-500/20 rounded-xl p-6 text-center">
                <p className="text-lg text-emerald-200">Ritmo actual</p>
                <p className="text-3xl font-bold text-emerald-100">€{Math.round(goal.production.daily_pace_current).toLocaleString("es-ES")}</p>
              </div>
            </div>
          </div>
        )}

        {slide === "occupancy_week" && (
          <div className="h-full">
            <div className="flex items-center gap-4 mb-6">
              <Users className="w-12 h-12 text-emerald-300" />
              <h2 className="text-5xl font-bold">Ocupación próximos 7 días</h2>
            </div>
            <div className="grid grid-cols-7 gap-3">
              {forecast.slice(0, 7).map((f) => (
                <div key={f.date} className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-sm text-slate-300">{f.day_name}</p>
                  <p className="text-xs text-slate-400">{f.date.slice(8, 10)}/{f.date.slice(5, 7)}</p>
                  <p className="text-4xl font-bold mt-2" style={{
                    color: f.predicted_occupancy >= 85 ? "#10b981" : f.predicted_occupancy >= 60 ? "#fbbf24" : "#ef4444"
                  }}>
                    {Math.round(f.predicted_occupancy)}%
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {slide === "weather" && weather?.forecast && (
          <div className="h-full">
            <div className="flex items-center gap-4 mb-6">
              <Cloud className="w-12 h-12 text-blue-300" />
              <h2 className="text-5xl font-bold">Tiempo Sitges 7 días</h2>
            </div>
            <div className="grid grid-cols-7 gap-3">
              {weather.forecast.slice(0, 7).map((d) => (
                <div key={d.date} className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-sm text-slate-300">{new Date(d.date).toLocaleDateString("es-ES", { weekday: "short" })}</p>
                  <p className="text-5xl my-2">{d.emoji}</p>
                  <p className="text-2xl font-bold">{d.temp_max}°</p>
                  <p className="text-sm text-slate-400">{d.temp_min}°</p>
                  <p className="text-xs mt-1 text-slate-300">{d.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {slide === "events" && (
          <div className="h-full">
            <div className="flex items-center gap-4 mb-6">
              <Calendar className="w-12 h-12 text-purple-300" />
              <h2 className="text-5xl font-bold">Eventos próximos 14 días</h2>
            </div>
            <div className="space-y-3">
              {events.length > 0 ? events.slice(0, 5).map((e) => (
                <div key={e.id || e.name} className="bg-white/10 rounded-xl p-5 flex justify-between items-center">
                  <div>
                    <p className="text-2xl font-bold">{e.name}</p>
                    <p className="text-lg text-slate-300">{e.start_date} → {e.end_date} · {e.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-purple-300">+{e.demand_impact}%</p>
                    <p className="text-sm text-slate-400">impacto demanda</p>
                  </div>
                </div>
              )) : (
                <p className="text-2xl text-slate-400 text-center py-12">Sin eventos relevantes próximamente</p>
              )}
            </div>
          </div>
        )}

        {slide === "suggestions" && suggestions?.suggestions && (
          <div className="h-full">
            <div className="flex items-center gap-4 mb-6">
              <TrendingUp className="w-12 h-12 text-amber-300" />
              <h2 className="text-5xl font-bold">Sugerencias IA del día</h2>
            </div>
            <div className="space-y-4">
              {suggestions.suggestions.slice(0, 3).map((s) => (
                <div key={s.title || s.action} className="bg-white/10 rounded-xl p-6">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-2xl font-bold">{s.title}</p>
                    <p className="text-2xl font-bold text-emerald-300">+€{s.estimated_impact_eur?.toLocaleString("es-ES")}</p>
                  </div>
                  <p className="text-lg text-slate-300">{s.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Slide indicators */}
      <div className="flex justify-center gap-2 mt-6">
        {SLIDES.map((s, idx) => (
          <button
            key={s}
            type="button"
            onClick={() => setCurrentSlide(idx)}
            className={`h-2 rounded-full transition-all ${idx === currentSlide ? "w-12 bg-white" : "w-2 bg-white/30"}`}
            aria-label={`slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TVMode;
